import { computed, inject, Injectable, signal } from '@angular/core';
import { EMPTY, Observable, finalize, tap } from 'rxjs';
import { AnalyticsEvents } from '@app/core/enums/analytics-events.enum';
import { ProcessNames, ProcessSteps } from '@app/core/enums/process.enum';
import { FeatureFlagStore } from '@app/core/feature-flags/feature-flag.store';
import { FeatureFlags } from '@app/core/feature-flags/feature-flag.types';
import { AnalyticsService } from '@app/core/services/analytics.service';
import { PolicyService } from '@app/core/services/policy.service';
import { PolicyConditions } from '@app/core/types/policy.types';
import { SharingBrand } from '@app/core/types/account-state.types';
import { ShareDataService } from '../services/share-data.service';
import { ProfileConsents } from '../model/share-data.types';

@Injectable({ providedIn: 'root' })
export class ShareDataSandbox {
  private readonly shareDataService = inject(ShareDataService);
  private readonly policyService = inject(PolicyService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly featureFlagStore = inject(FeatureFlagStore);

  brands = signal<SharingBrand[]>([]);
  termsAndConditions = signal<PolicyConditions | null>(null);
  privacyPolicy = signal<PolicyConditions | null>(null);
  sharing = signal(false);
  skipping = signal(false);

  submitting = computed(() => this.sharing() || this.skipping());
  policiesLoaded = computed(() => this.termsAndConditions()?.id != null && this.privacyPolicy() !== null);

  readonly marketingConsentEnabled = this.featureFlagStore.getFeatureFlag(FeatureFlags.MarketingConsent);

  setBrands(brands: SharingBrand[]): void {
    this.brands.set(brands ?? []);
  }

  loadPolicies(): void {
    this.policyService.getTermsAndConditions().subscribe((terms) => this.termsAndConditions.set(terms));
    this.policyService.getPrivacyPolicy().subscribe((policy) => this.privacyPolicy.set(policy));
  }

  shareData(marketing: boolean): Observable<void> {
    if (this.submitting()) {
      return EMPTY;
    }

    this.sharing.set(true);
    return this.shareDataService
      .linkExistingBrands(this.consents(marketing))
      .pipe(
        tap(() => this.logShareDataCompleted()),
        finalize(() => this.sharing.set(false))
      );
  }

  skipSharing(marketing: boolean): Observable<void> {
    if (this.submitting()) {
      return EMPTY;
    }

    this.skipping.set(true);
    return this.shareDataService
      .setupBrandProfile(this.consents(marketing))
      .pipe(
        tap(() => this.logShareDataCompleted()),
        finalize(() => this.skipping.set(false))
      );
  }

  logShareDataStart(): void {
    this.analyticsService.logEvent(AnalyticsEvents.Process, {
      name: ProcessNames.ShareData,
      step: ProcessSteps.ShareDataStart,
    });
  }

  private consents(marketing: boolean): ProfileConsents {
    return {
      termsConditionsId: this.termsAndConditions()?.id ?? '',
      // Absent when the brand doesn't ask, so the API can tell "declined" from "not asked".
      ...(this.marketingConsentEnabled() && { marketing }),
    };
  }

  private logShareDataCompleted(): void {
    this.analyticsService.logEvent(AnalyticsEvents.ProcessCompleted, {
      name: ProcessNames.ShareData,
    });
  }
}
